import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchAdminGdComments, replyAdminGdComment, deleteAdminGdComment } from "./adminGdService";

const formatDate = (dt) => dt ? new Date(dt).toLocaleString() : "";

const CommentNode = ({ comment, topicId, onReply, onDelete, depth = 0 }) => {
  const [replyText, setReplyText] = useState("");
  const [showReply, setShowReply] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    await onReply(comment.id, replyText.trim());
    setReplyText("");
    setShowReply(false);
  };

  return (
    <div style={{ marginLeft: depth * 24, borderLeft: depth > 0 ? "2px solid #e0e0e0" : "none", paddingLeft: depth > 0 ? 12 : 0, marginBottom: 12 }}>
      <div style={{ background: "#f9f9f9", borderRadius: 8, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <strong style={{ fontSize: 13 }}>{comment.authorName}</strong>
            <span style={{
              fontSize: 11, padding: "1px 8px", borderRadius: 999, fontWeight: 600,
              background: comment.authorType === "ADMIN" ? "#e8f5e9" : "#e3f2fd",
              color: comment.authorType === "ADMIN" ? "#2e7d32" : "#1565c0",
            }}>{comment.authorType}</span>
            <span style={{ fontSize: 11, color: "#999" }}>{formatDate(comment.createdAt)}</span>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setShowReply((s) => !s)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #019095", color: "#019095", background: "none", cursor: "pointer" }}>
              Reply
            </button>
            <button onClick={() => onDelete(comment.id)} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, border: "1px solid #e53935", color: "#e53935", background: "none", cursor: "pointer" }}>
              Delete
            </button>
          </div>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: "#333" }}>{comment.commentText}</p>
        {showReply && (
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <input
              value={replyText} onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a reply..."
              style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13 }}
              onKeyDown={(e) => e.key === "Enter" && handleReply()}
            />
            <button onClick={handleReply} style={{ padding: "6px 14px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Send</button>
            <button onClick={() => setShowReply(false)} style={{ padding: "6px 10px", border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          </div>
        )}
      </div>
      {comment.replies?.map((reply) => (
        <CommentNode key={reply.id} comment={reply} topicId={topicId} onReply={onReply} onDelete={onDelete} depth={depth + 1} />
      ))}
    </div>
  );
};

const AdminGdCommentsPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchAdminGdComments(topicId, { page, size: 20 });
    if (res.status === 200 && res.data) {
      setComments(res.data.content || res.data || []);
      setTotalPages(res.data.totalPages || 0);
    }
    setLoading(false);
  }, [topicId, page]);

  useEffect(() => { load(); }, [load]);

  const handleReply = async (commentId, text) => {
    const res = await replyAdminGdComment(topicId, commentId, text);
    if (res.status === 200 || res.status === 201) load();
    else alert(res.message || "Reply failed");
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    const res = await deleteAdminGdComment(topicId, commentId);
    if (res.status === 200 || res.status === 204) load();
    else alert(res.message || "Delete failed");
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Comment Moderation</h2>
        <button onClick={() => navigate("/admin/gd/topics")} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>← Back to Topics</button>
      </div>

      {loading ? <p>Loading...</p> : comments.length === 0 ? (
        <p style={{ color: "#888" }}>No comments yet.</p>
      ) : (
        comments.map((c) => (
          <CommentNode key={c.id} comment={c} topicId={topicId} onReply={handleReply} onDelete={handleDelete} />
        ))
      )}

      {totalPages > 1 && (
        <div style={{ marginTop: 20 }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
          <span style={{ margin: "0 10px" }}>Page {page + 1} / {totalPages}</span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
};

export default AdminGdCommentsPage;
